import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Building2, Upload, Save, Image as ImageIcon, Phone, Mail, MapPin, Globe } from 'lucide-react';
import { toast } from 'sonner';
import DataHealthDashboard from '@/components/DataHealthDashboard';
import DataBackupManager from '@/components/DataBackupManager';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SchoolSettingsPage = () => {
  const { user, getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [schoolData, setSchoolData] = useState({
    name: '',
    code: '',
    email: '',
    phone: '',
    address: '',
    website: '',
    logo_url: '',
    current_session: ''
  });

  useEffect(() => {
    fetchSchoolData();
  }, []);

  const fetchSchoolData = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/schools/current`, { headers: getAuthHeader() });
      setSchoolData(res.data);
    } catch (error) {
      toast.error('Failed to load school data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo size should be less than 2MB');
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      // Upload logo via backend
      const res = await axios.post(
        `${API_URL}/api/schools/upload-logo`,
        formData,
        { 
          headers: { 
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (res.data.url) {
        setSchoolData({...schoolData, logo_url: res.data.url});
        toast.success('Logo uploaded successfully!');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/schools/current`,
        {
          name: schoolData.name,
          email: schoolData.email,
          phone: schoolData.phone,
          address: schoolData.address,
          website: schoolData.website,
          logo_url: schoolData.logo_url,
          current_session: schoolData.current_session
        },
        { headers: getAuthHeader() }
      );
      toast.success('School settings saved successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="School Settings">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="School Settings">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Building2 className="h-6 w-6" /> School Settings
            </h1>
            <p className="text-muted-foreground">Manage your school information and branding</p>
          </div>
          <Button onClick={saveSettings} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>

        {/* Logo Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ImageIcon className="h-5 w-5" /> School Logo
            </CardTitle>
            <CardDescription>
              This logo will appear on fee receipts, ID cards, admit cards, and other documents
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6">
              {/* Logo Preview */}
              <div className="w-32 h-32 border-2 border-dashed rounded-lg flex items-center justify-center bg-muted/50 overflow-hidden">
                {schoolData.logo_url ? (
                  <img 
                    src={schoolData.logo_url} 
                    alt="School Logo" 
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <div className="text-center text-muted-foreground">
                    <ImageIcon className="h-8 w-8 mx-auto mb-1" />
                    <p className="text-xs">No Logo</p>
                  </div>
                )}
              </div>
              
              {/* Upload Controls */}
              <div className="space-y-3">
                <div>
                  <input
                    type="file"
                    id="logo-upload"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    onClick={() => document.getElementById('logo-upload').click()}
                    disabled={uploading}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {uploading ? 'Uploading...' : 'Upload Logo'}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Recommended: Square image, PNG or JPG, max 2MB
                </p>
                {schoolData.logo_url && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-red-600"
                    onClick={() => setSchoolData({...schoolData, logo_url: ''})}
                  >
                    Remove Logo
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Basic Info */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>School Name</Label>
                <Input 
                  value={schoolData.name}
                  onChange={(e) => setSchoolData({...schoolData, name: e.target.value})}
                  placeholder="Enter school name"
                />
              </div>
              <div className="space-y-2">
                <Label>School Code</Label>
                <Input 
                  value={schoolData.code}
                  disabled
                  className="bg-muted"
                />
                <p className="text-xs text-muted-foreground">School code cannot be changed</p>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Current Academic Session</Label>
              <Input 
                value={schoolData.current_session}
                onChange={(e) => setSchoolData({...schoolData, current_session: e.target.value})}
                placeholder="e.g., 2025-26"
              />
            </div>
          </CardContent>
        </Card>

        {/* Contact Info */}
        <Card>
          <CardHeader>
            <CardTitle>Contact Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Mail className="h-4 w-4" /> Email
                </Label>
                <Input 
                  type="email"
                  value={schoolData.email || ''}
                  onChange={(e) => setSchoolData({...schoolData, email: e.target.value})}
                  placeholder="school@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Phone className="h-4 w-4" /> Phone
                </Label>
                <Input 
                  value={schoolData.phone || ''}
                  onChange={(e) => setSchoolData({...schoolData, phone: e.target.value})}
                  placeholder="+91 9876543210"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Globe className="h-4 w-4" /> Website
              </Label>
              <Input 
                value={schoolData.website || ''}
                onChange={(e) => setSchoolData({...schoolData, website: e.target.value})}
                placeholder="https://www.yourschool.com"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <MapPin className="h-4 w-4" /> Address
              </Label>
              <Textarea 
                value={schoolData.address || ''}
                onChange={(e) => setSchoolData({...schoolData, address: e.target.value})}
                placeholder="Full school address"
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Save Button at bottom */}
        <div className="flex justify-end">
          <Button onClick={saveSettings} disabled={saving} size="lg">
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save All Changes'}
          </Button>
        </div>

        {/* Data Health Dashboard */}
        <DataHealthDashboard />

        {/* Data Backup Manager */}
        <DataBackupManager />
      </div>
    </DashboardLayout>
  );
};

export default SchoolSettingsPage;
